import { useApolloClient } from '@apollo/client';
import { Col, Row, Form, message } from 'antd';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type WebCalcSettingDataFragment } from '../../../../api/fragments/WebCalcSettingData';
import {
    CreateWebCalcSettingDocument,
    type CreateWebCalcSettingMutation,
    type CreateWebCalcSettingMutationVariables,
} from '../../../../api/mutations/createWebCalcSetting';
import {
    UpdateWebCalcSettingDocument,
    type UpdateWebCalcSettingMutation,
    type UpdateWebCalcSettingMutationVariables,
} from '../../../../api/mutations/updateWebCalcSetting';
import { type WebCalcSettingPayload } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

const colSpan = { xs: 24 };
const FormFields = () => {
    const { t } = useTranslation(['bankModuleDetails']);

    return (
        <Row gutter={14}>
            <Col {...colSpan}>
                <InputField
                    {...t('bankModuleDetails:remoteFpSetting.fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankModuleDetails:remoteFpSetting.fields.domain', { returnObjects: true })}
                    name="domain"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    addonBefore="https://"
                    {...t('bankModuleDetails:remoteFpSetting.fields.calculationUrl', { returnObjects: true })}
                    name="calculationUrl"
                    required
                />
            </Col>
        </Row>
    );
};

export type RemoteFPSettingFormProps = {
    moduleId: string;
    onComplete: () => void;
    setting?: WebCalcSettingDataFragment;
};

type FormValues = WebCalcSettingPayload;
const DEFAULT_INITIAL_VALUES: FormValues = {
    displayName: '',
    domain: '',
    calculationUrl: '',
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('domain'),
    validators.requiredString('calculationUrl')
);

const RemoteFPSettingForm = ({ moduleId, onComplete, setting }: RemoteFPSettingFormProps) => {
    const { t } = useTranslation(['bankModuleDetails']);

    const apolloClient = useApolloClient();
    const onSubmit = useHandleError<FormValues>(
        async values => {
            const isEdit = !!values.id;

            // submitting message
            message.loading({
                content: t(`bankModuleDetails:remoteFpSetting.drawer.messages.${isEdit ? 'updating' : 'creating'}`),
                key: 'primary',
                duration: 0,
            });

            const variables = {
                moduleId,
                setting: values,
            };

            if (isEdit) {
                await apolloClient
                    .mutate<UpdateWebCalcSettingMutation, UpdateWebCalcSettingMutationVariables>({
                        mutation: UpdateWebCalcSettingDocument,
                        variables,
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });
            } else {
                await apolloClient
                    .mutate<CreateWebCalcSettingMutation, CreateWebCalcSettingMutationVariables>({
                        mutation: CreateWebCalcSettingDocument,
                        variables,
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });
            }

            message.success({
                content: t(`bankModuleDetails:remoteFpSetting.drawer.messages.${isEdit ? 'updated' : 'created'}`),
                key: 'primary',
            });

            onComplete();
        },
        [t, moduleId, onComplete, apolloClient]
    );

    const initialValues = useMemo<FormValues>(() => setting ?? DEFAULT_INITIAL_VALUES, [setting]);

    const validate = useValidator(validator);

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="remoteFPSettingForm"
                    layout="vertical"
                    name="remoteFPSettingForm"
                    onSubmitCapture={handleSubmit}
                >
                    <FormFields />
                </Form>
            )}
        </Formik>
    );
};

export default RemoteFPSettingForm;
